import PropTypes from 'prop-types';
import {
  Skeleton,
  useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';
import Checkout from '../Payment/Checkout';
import Modal from '../Layout/Modal';
import useAuthContext from '../../hooks/useAuthContext';
import { useNavigate } from 'react-router-dom';
import { EMAIL_CONFIRMATION } from '../../routes/app/paths';
import { useQueryClient } from '@tanstack/react-query';
import usePendingRecharge, { encodePr } from '../../hooks/usePendingRecharge';
import PaymentSuccessContent from '../Payment/PaymentSuccessContent';

function Item({ tier, special = false, isLoading = false }) {
  const { user, isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { savePendingProduct } = usePendingRecharge();

  const [showSelect, setShowSelect] = useState(false);
  const {
    isOpen: isOpenSuccess,
    onOpen: onOpenSuccess,
    onClose: onCloseSuccess,
  } = useDisclosure();

  const recharge = {
    rechargeable_id: tier.rechargeable_id || tier.id || null,
    rechargeable_type: tier.rechargeable_type || 'Product',
    cost_points: tier.cost_points || 0,
  };

  /* Functions */
  const handleOnSuccess = () => {
    setShowSelect(false);
    onOpenSuccess()
    queryClient.removeQueries('paymentStripeIntent');
  };

  if (isLoading)
    return (
      <div className="bg-white rounded-3xl p-6 shadow-md min-w-[250px] flex flex-col gap-4">
        <Skeleton width="40%" height="28px" borderRadius="full" startColor="gray.100" endColor="gray.200" />
        <Skeleton width="70%" height="36px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
        <Skeleton width="55%" height="22px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
        <div className="border-t border-gray-100 my-2" />
        <Skeleton width="45%" height="48px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
        <Skeleton width="60%" height="20px" borderRadius="md" startColor="gray.100" endColor="gray.200" />
        <Skeleton width="100%" height="52px" borderRadius="full" mt="4" startColor="gray.100" endColor="gray.200" />
      </div>
    );

  const handleBuy = () => {
    if (isAuthenticated && !user.email_verified_at) {
      return navigate(EMAIL_CONFIRMATION);
    }
    // Si no está autenticado, guardar el producto para restaurarlo en el Hero tras el login
    if (!isAuthenticated && recharge.rechargeable_id) {
      savePendingProduct(encodePr(recharge.rechargeable_id, recharge.rechargeable_type));
    }
    setShowSelect(true);
  }

  return (
    <>
      <div
        key={tier.id}
        className="bg-white rounded-3xl p-6 shadow-md hover:shadow-xl transition duration-200 ease-in-out transform hover:-translate-y-1 min-w-[250px] flex flex-col"
      >
        {/* Badge */}
        {tier.type && (
          <span className="self-start bg-[#e6f4f3] text-primary text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            {tier.type}
          </span>
        )}

        {/* Name & description */}
        <h3 className="text-[1.7rem] font-extrabold text-gray-900 leading-tight">
          {tier.name}
        </h3>
        {tier.description && (
          <p className="text-gray-500 text-base font-medium mt-1">
            {tier.description}
          </p>
        )}

        {/* Promo */}
        {tier.promo && (
          <div className="mt-2">
            <p className="text-primary font-medium text-sm">{tier.promo.title}</p>
            <p className="text-gray-400 font-normal text-xs">{tier.promo.description}</p>
          </div>
        )}

        <hr className="border-gray-100 my-4" />

        {/* Price */}
        <div className="flex items-baseline gap-2">
          <span className="text-5xl font-extrabold text-primary">
            {tier.price_eur || tier.offer_price_eur}€
          </span>
          <span className="text-gray-400 text-base font-normal">/ recarga</span>
        </div>

        {/* Features */}
        {tier.features && tier.features.length > 0 && (
          <ul className="mt-3 space-y-1">
            {tier.features.map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-gray-500 text-sm">
                <span className="text-gray-400">•</span>
                {feature}
              </li>
            ))}
          </ul>
        )}

        {/* Validity */}
        {tier.valid_until && (
          <p className="mt-3 flex items-center gap-1.5 text-gray-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
            </svg>
            Válida hasta {tier.valid_until}
          </p>
        )}

        {/* CTA */}
        <button
          aria-describedby={tier.id}
          className="mt-6 w-full bg-primary hover:bg-teal-700 text-white text-lg font-semibold py-3.5 rounded-full transition duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          onClick={handleBuy}
        >
          Recargar ahora
        </button>
      </div>

      {/* Modals */}
      <Modal
        show={
          showSelect &&
          recharge.rechargeable_id !== null &&
          recharge.rechargeable_type !== null
        }
        onClose={() => setShowSelect(false)}
        size='none'
      >
        <Checkout
          recharge={recharge}
          type={tier.type}
          onClose={() => {
            setShowSelect(false)
            queryClient.removeQueries('paymentStripeIntent');
          }}
          onSuccess={handleOnSuccess}
          prevCheckoutData={{
            rechargeable_object: {
              name: tier.name,
              description: tier.description,
            },
            rechargeable_type: tier.rechargeable_type,
            type: tier.type,
          }}
        />
      </Modal>

      {/* Modal success */}
      <Modal show={isOpenSuccess} onClose={onCloseSuccess}>
        <PaymentSuccessContent onClose={onCloseSuccess} />
      </Modal>
    </>
  );
}

Item.propTypes = {
  tier: PropTypes.object.isRequired,
  special: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default Item;
